import { apiClient } from '@/api/client'
import {
  DRIVE_SUMMARY,
  DRIVE_UNPLACED,
  driveFilesMovePath,
  driveFilesPath,
  driveFolderPath,
  driveFoldersPath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { UserDocument } from './documents.service'

/** Stands in for a folder id when listing files that sit in no folder. */
export const UNFILED = 'unfiled'
/** Read-only views of files that belong to a person. */
export const EMPLOYEE_FILES = 'employees'
export const CANDIDATE_FILES = 'candidates'
/** The default folder that also shows candidate files, whatever it has been renamed to. */
export const RECRUITMENT_KEY = 'recruitment'

export interface DriveFolder {
  id: string
  parentId: string | null
  name: string
  /** A standard folder: can be renamed, not deleted. */
  isDefault: boolean
  systemKey: string | null
  fileCount: number
  totalSize: number
  lastFileAt: string | null
}

/** A company's library totals, for the list of companies. */
export interface DriveCompanySummary {
  companyId: string
  fileCount: number
  totalSize: number
  lastFileAt: string | null
}

export interface DriveFolderList {
  folders: DriveFolder[]
  unfiledCount: number
  /** Left out for people who can't see other people's files. */
  employeeFileCount?: number
  candidateFileCount?: number
}

/** An older employee file with no company recorded, waiting for HR to place it. */
export interface UnplacedFile {
  id: string
  originalName: string
  category: string
  size: number
  createdAt?: string
  owner: { id: string; name: string }
  ownerCompanies: { id: string; name: string }[]
}

export interface UnplacedFilesPage {
  data: UnplacedFile[]
  pagination: { total: number; page: number; limit: number }
}

export interface ListDriveFilesParams {
  /** A folder id or {@link UNFILED}; left out to cover the whole company. */
  folderId?: string
  search?: string
  includeArchived?: boolean
  page: number
  limit: number
}

export interface DriveFilesPage {
  data: UserDocument[]
  pagination: { total: number; page: number; limit: number }
}

export async function listDriveCompanySummaries(): Promise<DriveCompanySummary[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<DriveCompanySummary[]>>(DRIVE_SUMMARY)
  return Array.isArray(data?.data) ? data.data : []
}

export async function listDriveFolders(companyId: string): Promise<DriveFolderList> {
  const { data } = await apiClient.get<ApiResponseWrapper<DriveFolderList>>(
    driveFoldersPath(companyId),
  )
  return data.data
}

export async function createDriveFolder(
  companyId: string,
  input: { name: string; parentId?: string },
): Promise<void> {
  await apiClient.post(driveFoldersPath(companyId), input)
}

export async function renameDriveFolder(
  companyId: string,
  id: string,
  name: string,
): Promise<void> {
  await apiClient.patch(driveFolderPath(companyId, id), { name })
}

export async function deleteDriveFolder(companyId: string, id: string): Promise<void> {
  await apiClient.delete(driveFolderPath(companyId, id))
}

export async function moveDriveFiles(
  companyId: string,
  fileIds: string[],
  folderId: string,
): Promise<void> {
  await apiClient.post(driveFilesMovePath(companyId), { fileIds, folderId })
}

export async function listUnplacedFiles(page: number, limit: number): Promise<UnplacedFilesPage> {
  const { data } = await apiClient.get<ApiResponseWrapper<UnplacedFilesPage>>(DRIVE_UNPLACED, {
    params: { page, limit },
  })
  return data.data
}

export async function placeFileInCompany(id: string, companyId: string): Promise<void> {
  await apiClient.patch(`${DRIVE_UNPLACED}/${id}`, { companyId })
}

export async function listDriveFiles(
  companyId: string,
  params: ListDriveFilesParams,
): Promise<DriveFilesPage> {
  const { data } = await apiClient.get<ApiResponseWrapper<DriveFilesPage>>(
    driveFilesPath(companyId),
    { params },
  )
  return data.data
}
